// modules/website/components/ComponentRenderer.tsx - Renderizador Universal de Componentes

import React from 'react';
import { PageComponent, ImageData } from '@/types';
import { HeroComponent } from './HeroComponent';
import { ServicesComponent } from './ServicesComponent';
import { GalleryComponent } from './GalleryComponent';
import { ContactFormComponent } from './ContactFormComponent';
import { TestimonialsComponent } from './TestimonialsComponent';
import { PricingComponent } from './PricingComponent';
import { TeamComponent } from './TeamComponent';
import { RichTextComponent } from './RichTextComponent';
// Fase 1 - Quick Wins
import { VideoComponent } from './VideoComponent';
import { SpacerComponent } from './SpacerComponent';
import { ButtonComponent } from './ButtonComponent';
import { EmbedComponent } from './EmbedComponent';
// Fase 2 - Widgets de Pesca
import { TrophyGalleryComponent } from './TrophyGalleryComponent';
import { FishSpeciesComponent } from './FishSpeciesComponent';
import { FishingCalendarComponent } from './FishingCalendarComponent';
import { SocialShareComponent } from './SocialShareComponent';
// Fase 3 - Widgets Avançados
import { BookingWidgetComponent } from './BookingWidgetComponent';
import { WeatherWidgetComponent } from './WeatherWidgetComponent';
import { LiveChatComponent } from './LiveChatComponent';

interface Props {
  component: PageComponent;
}

export const ComponentRenderer: React.FC<Props> = ({ component }) => {
  const { type, data, settings } = component;

  // Renderizar componente baseado no tipo
  switch (type) {
    case 'hero':
      return <HeroComponent data={data as any} settings={settings} />;
      
    case 'services':
    case 'features':
    case 'about':
      return <ServicesComponent data={data as any} settings={settings} />;
      
    case 'gallery':
      return <GalleryComponent data={data as any} settings={settings} />;
      
    case 'contact':
    case 'contact_form':
    case 'contact-form':
      return <ContactFormComponent data={data as any} settings={settings} />;
      
    case 'testimonials':
    case 'reviews':
      return <TestimonialsComponent data={data as any} settings={settings} />;
      
    case 'pricing':
    case 'tariff':
      return <PricingComponent data={data as any} settings={settings} />;
      
    case 'team':
    case 'guides':
      return <TeamComponent data={data as any} settings={settings} />;
      
    case 'rich_text':
    case 'text':
    case 'text-block':
      return <RichTextComponent data={data as any} settings={settings} />;

    // === FASE 1 - QUICK WINS ===
    
    case 'video':
      return <VideoComponent data={data as any} settings={settings} />;
      
    case 'spacer':
    case 'divider':
      return <SpacerComponent data={data as any} settings={settings} />;
      
    case 'button':
    case 'cta':
      return <ButtonComponent data={data as any} settings={settings} />;
      
    case 'embed':
    case 'map':
      return <EmbedComponent data={data as any} settings={settings} />;

    // === FASE 2 - WIDGETS DE PESCA ===
    
    case 'trophy_gallery':
      return <TrophyGalleryComponent data={data as any} settings={settings} />;
      
    case 'species':
    case 'fish-species':
      return <FishSpeciesComponent data={data as any} settings={settings} />;
      
    case 'fishing_calendar':
      return <FishingCalendarComponent data={data as any} settings={settings} />;
      
    case 'social_share':
      return <SocialShareComponent data={data as any} settings={settings} />;

    // === FASE 3 - WIDGETS AVANÇADOS ===
    
    case 'booking':
    case 'booking_widget':
    case 'reservation':
      return <BookingWidgetComponent data={data as any} settings={settings} />;
      
    case 'weather':
    case 'weather_widget':
      return <WeatherWidgetComponent data={data as any} settings={settings} />;
      
    case 'live_chat':
    case 'chat':
    case 'whatsapp':
      return <LiveChatComponent data={data as any} />;

    case 'image':
      const imageData = data as ImageData;
      return (
        <div 
          className="py-8 px-6"
          style={{ backgroundColor: settings?.backgroundColor }}
        >
          <div className={`max-w-4xl mx-auto ${
            imageData.alignment === 'center' ? 'text-center' :
            imageData.alignment === 'right' ? 'text-right' :
            'text-left'
          }`}>
            <img 
              src={imageData.src || '/placeholder.jpg'} 
              alt={imageData.alt || ''}
              className="rounded-lg shadow-lg"
              style={{ 
                width: imageData.width || 'auto',
                height: imageData.height || 'auto'
              }}
            />
            {imageData.caption && (
              <p className="text-gray-600 mt-4 italic">{imageData.caption}</p>
            )}
          </div>
        </div>
      );
      
    default:
      // Componente desconhecido - renderizar placeholder
      return (
        <div className="py-12 px-6 bg-gray-100 border-2 border-dashed border-gray-300">
          <div className="max-w-4xl mx-auto text-center text-gray-500">
            <p className="text-lg">Componente "{type}" não implementado ainda</p>
            <p className="text-sm mt-2">Adicione a implementação em ComponentRenderer.tsx</p>
          </div>
        </div>
      );
  }
};
